<script module>
	export { PopupContent };
</script>

<script lang="ts" generics="PopupPropsType extends { id: string }">
	import CardBadge from '$lib/components/ui/CardBadge.svelte';
	import type { PopupController } from '$lib/components/ui/map/popup/popup.controller.svelte';
	import type { PopupContentProps } from '$lib/components/ui/map/popup/popupProps';
	import { flattenCoordinates } from '$lib/features/_other/coordinates.type';
	import { getStreetViewUrl } from '$lib/utils/geo.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import type { Snippet } from 'svelte';
	import { Popup } from 'svelte-maplibre-gl';
	import IconClose from '~icons/mdi/close';
	import IconStreetView from '~icons/mdi/google-street-view';

	const {
		controller,
		overrideHandleClose,
		ContentSnippet
	}: {
		controller: PopupController<PopupPropsType>;
		overrideHandleClose?: () => void;
		ContentSnippet: Snippet;
	} = $props();

	function handleClose(): void {
		if (overrideHandleClose) {
			overrideHandleClose();
		} else {
			controller.closePopup('click');
		}
	}
</script>

<Popup
	class="z-100"
	lnglat={flattenCoordinates(controller.popup.coordinates)}
	open={controller.popup.open.isOpened || false}
	offset={3}
	focusAfterOpen={false}
	closeButton={false}
	closeOnClick={false}
>
	<div
		class="flex h-full min-h-0 flex-col gap-2 bg-base-100 p-3 text-base-content outline-none"
		role="tooltip"
		onmouseenter={() => controller.onPopupMouseEnter()}
		onmouseleave={() => controller.onPopupMouseLeave()}
	>
		{#if controller.popup.open.origin !== 'hover'}
			{@render CloseBtn()}
		{/if}
		{#if controller.popup.props}
			{@render ContentSnippet()}
		{:else}
			<div class="p-2 text-center">No info</div>
		{/if}
	</div>
</Popup>

{#snippet CloseBtn()}
	<button
		class="absolute top-1 right-1 z-20 flex size-7 items-center justify-center rounded-full bg-error text-error-content shadow-md transition-all hover:scale-105 hover:bg-error/85 active:scale-95"
		aria-label="Close popup"
		onclick={handleClose}
	>
		<IconClose class="size-4" />
	</button>
{/snippet}

{#snippet PopupContent(props: PopupContentProps | undefined, noPropsText: string = 'No info')}
	{#if props}
		<div class="absolute inset-0 z-0 flex flex-col justify-end overflow-hidden rounded-box bg-neutral-900">
			{@render Header(props)}

			{#if props.badges && props.badges.length > 0}
				<div class="absolute top-1.5 left-1.5 z-10 flex flex-col items-start gap-1">
					{#each props.badges as badge}
						<CardBadge classes="text-[10px] font-mono py-0.5 px-1.5 shadow-sm" text={badge} />
					{/each}
				</div>
			{/if}

			<div class="relative z-10 flex flex-col gap-1 bg-linear-to-t from-black/90 via-black/55 to-transparent p-2 pt-7 text-white">
				<h4 class="line-clamp-1 text-sm font-bold text-white drop-shadow-md" title={props.label}>
					{props.label}
				</h4>
				{#if props.cta && props.streetView}
					<div class="flex shrink-0 flex-row gap-2">
						{@render CtaLink(props.cta.label, props.cta.href)}
						{@render StreetViewLink(props.label, props.streetView)}
					</div>
				{:else if props.cta}
					{@render CtaLink(props.cta.label, props.cta.href)}
				{:else if props.streetView}
					{@render StreetViewLink(props.label, props.streetView)}
				{/if}
			</div>
		</div>
	{:else}
		<div class="flex h-full items-center justify-center p-2 text-center">{noPropsText}</div>
	{/if}
{/snippet}

{#snippet Header(props: PopupContentProps)}
	{const imageUrl = $derived<string | null>(props.image.filename ? UrlUtil.getFullImageUrl(props.image.filename) : null)}
	{#if imageUrl}
		<img class="absolute inset-0 h-full w-full object-cover" src={imageUrl} alt="" draggable="false" />
	{:else}
		<div class="absolute inset-0 flex items-center justify-center bg-base-300 text-base-content/40">
			<props.image.IconOnNoImage class="text-5xl" />
		</div>
	{/if}
{/snippet}

{#snippet CtaLink(label: string, href: string)}
	<div class="min-w-0 flex-1">
		<a class="btn w-full shrink-0 font-bold btn-primary btn-xs" href={`/${href}`}>
			{label}
		</a>
	</div>
{/snippet}

{#snippet StreetViewLink(label: string, { latitude, longitude }: { latitude: number; longitude: number })}
	<div>
		<a
			class="btn btn-circle bg-black/40 text-white btn-xs hover:bg-white/40 hover:text-black"
			href={getStreetViewUrl({ latitude, longitude })}
			target="_blank"
			rel="noopener noreferrer"
			aria-label="Open {label} in Google Street View"
			title="Open {label} in Google Street View"
		>
			<IconStreetView />
		</a>
	</div>
{/snippet}

<style>
	/* Override the default popup styles, as hovering over the popup padding causes the popup to close too fast */
	:global(div.maplibregl-popup-content) {
		padding: 0 !important;
		border-radius: 16px !important;
		overflow: hidden !important;
		width: 80cqw !important;
		height: 45cqh !important;
	}
	:global(div.maplibregl-popup-content > div) {
		width: 100% !important;
		height: 100% !important;
	}
	@media (min-width: 768px) {
		:global(div.maplibregl-popup-content) {
			width: 20cqw !important;
		}
	}
</style>
